import { useState, useEffect } from "react";
import { disruptionAPI } from "../../lib/api";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { ScrollArea } from "../ui/scroll-area";
import { toast } from "sonner";
import { Users, GripVertical, Plus, Trash2, Save, Pencil, Check, Shuffle, Loader2 } from "lucide-react";

const TEAM_COLORS = [
  { bg: "bg-indigo-50", border: "border-indigo-200", text: "text-indigo-700", dot: "bg-indigo-500" },
  { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", dot: "bg-rose-500" },
  { bg: "bg-cyan-50", border: "border-cyan-200", text: "text-cyan-700", dot: "bg-cyan-500" },
  { bg: "bg-violet-50", border: "border-violet-200", text: "text-violet-700", dot: "bg-violet-500" },
];

const AVATAR_COLORS = ["bg-indigo-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-cyan-500", "bg-violet-500", "bg-orange-500", "bg-teal-500"];
const getAC = (n) => AVATAR_COLORS[Math.abs([...(n||"")].reduce((h,c)=>c.charCodeAt(0)+((h<<5)-h),0)) % AVATAR_COLORS.length];

const TeamBuilder = ({ open, onOpenChange, boardId, session, participants, onUpdate }) => {
  const [groups, setGroups] = useState([]);
  const [unassigned, setUnassigned] = useState([]);
  const [saving, setSaving] = useState(false);
  const [editingName, setEditingName] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [dragUser, setDragUser] = useState(null);
  const [dragFrom, setDragFrom] = useState(null);

  useEffect(() => {
    if (!open) return;
    const existing = session?.groups || [];
    setGroups(existing.map((g) => ({ ...g, members: [...(g.members || [])] })));
    // Find unassigned
    const assigned = new Set(existing.flatMap((g) => (g.members || []).map((m) => m.user_id)));
    setUnassigned(participants.filter((p) => !assigned.has(p.user_id)).map((p) => ({ user_id: p.user_id, name: p.name })));
  }, [open, session, participants]);

  const addTeam = () => {
    setGroups([...groups, {
      id: `team-${Date.now()}`,
      name: `Team ${groups.length + 1}`,
      members: [],
    }]);
  };

  const removeTeam = (idx) => {
    const removed = groups[idx];
    setUnassigned((prev) => [...prev, ...(removed.members || [])]);
    setGroups(groups.filter((_, i) => i !== idx));
  };

  const startEditName = (idx) => {
    setEditingName(idx);
    setNameInput(groups[idx].name);
  };

  const saveName = (idx) => {
    setGroups(groups.map((g, i) => i === idx ? { ...g, name: nameInput } : g));
    setEditingName(null);
  };

  const moveUser = (userId, fromType, fromIdx, toType, toIdx) => {
    let user;
    if (fromType === "unassigned") {
      user = unassigned.find((u) => u.user_id === userId);
      setUnassigned((prev) => prev.filter((u) => u.user_id !== userId));
    } else {
      user = groups[fromIdx]?.members?.find((m) => m.user_id === userId);
      setGroups(groups.map((g, i) => i === fromIdx ? { ...g, members: g.members.filter((m) => m.user_id !== userId) } : g));
    }
    if (!user) return;
    if (toType === "unassigned") {
      setUnassigned((prev) => [...prev, user]);
    } else {
      setGroups((prev) => prev.map((g, i) => i === toIdx ? { ...g, members: [...g.members, user] } : g));
    }
  };

  const autoAssign = () => {
    const allPeople = [...unassigned, ...groups.flatMap((g) => g.members || [])];
    const shuffled = [...allPeople].sort(() => Math.random() - 0.5);
    const teamCount = Math.max(groups.length, Math.ceil(shuffled.length / 4));
    const newGroups = Array.from({ length: teamCount }, (_, i) => ({
      id: groups[i]?.id || `team-${Date.now()}-${i}`,
      name: groups[i]?.name || `Team ${i + 1}`,
      members: [],
    }));
    shuffled.forEach((p, i) => newGroups[i % teamCount].members.push(p));
    setGroups(newGroups);
    setUnassigned([]);
    toast.success("Teams shuffled!");
  };

  const saveTeams = async () => {
    setSaving(true);
    try {
      await disruptionAPI.updateGroups(boardId, groups);
      toast.success("Teams saved!");
      onUpdate?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const handleDragStart = (userId, fromType, fromIdx) => {
    setDragUser(userId);
    setDragFrom({ type: fromType, idx: fromIdx });
  };

  const handleDrop = (toType, toIdx) => {
    if (dragUser && dragFrom) {
      moveUser(dragUser, dragFrom.type, dragFrom.idx, toType, toIdx);
    }
    setDragUser(null);
    setDragFrom(null);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[85vh] p-0" data-testid="team-builder-dialog">
        <div className="px-6 pt-5 pb-3 border-b border-slate-100">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <Users className="w-5 h-5 text-indigo-500" />
              Team Builder
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-400 mt-1">Drag participants between teams or use auto-assign. Name your teams.</p>
          <div className="flex items-center gap-2 mt-3">
            <Button variant="outline" size="sm" onClick={addTeam} className="h-7 text-xs">
              <Plus className="w-3 h-3 mr-1" />Add Team
            </Button>
            <Button variant="outline" size="sm" onClick={autoAssign} className="h-7 text-xs">
              <Shuffle className="w-3 h-3 mr-1" />Auto-Assign
            </Button>
            <Badge variant="outline" className="text-[10px]">{groups.length} teams</Badge>
            <Badge variant="outline" className="text-[10px]">{unassigned.length} unassigned</Badge>
          </div>
        </div>

        <ScrollArea className="max-h-[55vh] px-6 py-4">
          {/* Unassigned Pool */}
          {unassigned.length > 0 && (
            <div
              className="bg-slate-50 rounded-xl border-2 border-dashed border-slate-300 p-3 mb-4"
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop("unassigned", null)}
              data-testid="unassigned-pool"
            >
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-2 block">Unassigned</span>
              <div className="flex flex-wrap gap-1.5">
                {unassigned.map((u) => (
                  <div
                    key={u.user_id}
                    draggable
                    onDragStart={() => handleDragStart(u.user_id, "unassigned", null)}
                    className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-xs cursor-grab active:cursor-grabbing hover:shadow-sm"
                  >
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white ${getAC(u.name)}`}>
                      {(u.name||"?")[0].toUpperCase()}
                    </div>
                    <span className="text-slate-700">{u.name}</span>
                    <GripVertical className="w-3 h-3 text-slate-300" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Teams */}
          <div className="space-y-3">
            {groups.map((group, gIdx) => {
              const tc = TEAM_COLORS[gIdx % TEAM_COLORS.length];
              return (
                <div
                  key={group.id}
                  className={`${tc.bg} rounded-xl border ${tc.border} p-4`}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => handleDrop("group", gIdx)}
                  data-testid={`team-${gIdx}`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded-full ${tc.dot}`} />
                      {editingName === gIdx ? (
                        <div className="flex items-center gap-1">
                          <Input value={nameInput} onChange={(e) => setNameInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveName(gIdx)} className="h-7 w-36 text-xs bg-white" autoFocus />
                          <Button variant="ghost" size="sm" onClick={() => saveName(gIdx)} className="h-7 w-7 p-0"><Check className="w-3 h-3" /></Button>
                        </div>
                      ) : (
                        <span className={`font-heading font-semibold text-sm ${tc.text} cursor-pointer`} onClick={() => startEditName(gIdx)}>
                          {group.name}
                          <Pencil className="w-2.5 h-2.5 inline ml-1.5 opacity-40" />
                        </span>
                      )}
                      <Badge variant="outline" className="text-[9px]">{group.members?.length || 0} members</Badge>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeTeam(gIdx)} className="h-6 w-6 p-0 text-slate-400 hover:text-red-500">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-1.5 min-h-[36px]">
                    {(group.members || []).map((m) => (
                      <div
                        key={m.user_id}
                        draggable
                        onDragStart={() => handleDragStart(m.user_id, "group", gIdx)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-white border border-slate-200 text-xs cursor-grab active:cursor-grabbing hover:shadow-sm"
                      >
                        <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] font-bold text-white ${getAC(m.name)}`}>
                          {(m.name||"?")[0].toUpperCase()}
                        </div>
                        <span className="text-slate-700">{m.name}</span>
                        <GripVertical className="w-3 h-3 text-slate-300" />
                      </div>
                    ))}
                    {(group.members || []).length === 0 && (
                      <span className="text-[10px] text-slate-400 italic py-2">Drop participants here</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {groups.length === 0 && (
            <div className="text-center py-8">
              <Users className="w-8 h-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No teams yet. Add teams or use auto-assign.</p>
            </div>
          )}
        </ScrollArea>

        <div className="px-6 py-4 border-t border-slate-100">
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button data-testid="save-teams-btn" onClick={saveTeams} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white">
              {saving ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-1.5" />Save Teams</>}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TeamBuilder;
